import type { CaptureDateFilter, CaptureStatus } from '../../data/database.types';
import { CAPTURE_STATUS_LABEL, CAPTURE_STATUS_ORDER } from './capture-status';

/**
 * THE THREE CAPTURE FILTERS — M8 / S08-02.
 *
 * **EXACTLY THREE, AND THAT IS LOCKED.** `PRODUCT_TRUTH` §5 says "Capture
 * filters are exactly three chips: Location · Date · Status" and follows it
 * with "Do not invent additional primary navigation". The design export draws
 * the same three. A fourth chip is a product decision, not a convenience.
 *
 * **NOTHING HERE FILTERS ANYTHING.** This file holds the shape of a selection
 * and the words used to describe it. The narrowing is done by
 * `search_captures` in the database, which is the only place it may be done —
 * a second filtering engine in the browser would eventually disagree with the
 * first, and the disagreement would look like missing Captures.
 */

export interface CaptureFilters {
  /**
   * The APPLIED Search text — what retrieval was last asked for, not what is in
   * the box this instant. The screen debounces typing before it lands here, so
   * `d`, `dr`, `dry` never each become a request (M8 / S08-03).
   *
   * Empty string means no text query. It sits beside the three chips rather
   * than apart from them because Search and the filters **AND together**, and a
   * value that ANDs with the others belongs in the same object as the others.
   */
  readonly query: string;
  /**
   * The NORMALIZED key, never the display text.
   *
   * Two Captures can say "Unit 1207 Living Room" and "unit 1207 living room";
   * they share one key, so they are one option and one filter. The key is
   * internal and **never rendered** (migration 0010).
   */
  readonly locationKey: string | null;
  readonly date: CaptureDateFilter | null;
  readonly status: CaptureStatus | null;
}

export const NO_CAPTURE_FILTERS: CaptureFilters = {
  query: '',
  locationKey: null,
  date: null,
  status: null,
};

/** True when the Search box or at least one chip is narrowing the list. */
export function hasActiveCaptureFilters(filters: CaptureFilters): boolean {
  return (
    filters.query.trim() !== '' ||
    filters.locationKey !== null ||
    filters.date !== null ||
    filters.status !== null
  );
}

/**
 * How long the screen waits after the last keystroke before asking the database.
 *
 * Search runs AS BRANDON TYPES — there is no Enter to press, because the chips
 * beside it already react to a single tap and a Search box that needed a second
 * deliberate action would feel like a different product. The debounce is what
 * stops `d`, `dr`, `dry`, `dryw` each becoming a round trip.
 */
export const CAPTURE_SEARCH_DEBOUNCE_MS = 300;

/** The three Date buckets, in the order the design lists them. */
export const CAPTURE_DATE_ORDER: CaptureDateFilter[] = ['today', 'yesterday', 'earlier'];

export const CAPTURE_DATE_LABEL: Record<CaptureDateFilter, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  earlier: 'Earlier',
};

/**
 * The status word WITHOUT its glyph, and in sentence case.
 *
 * **Derived from the locked label rather than written out again**, so the
 * vocabulary has exactly one home. Two things are then removed for this
 * context:
 *
 *   * **the glyph**, which belongs to the status CHIP on a Capture card, where
 *     colour must never be the only signal — on a filter control it reads as
 *     punctuation, so the word carries the meaning instead;
 *   * **the shouting.** `● OPEN` is a chip; `Open` is a choice in a list. The
 *     design export makes the same distinction, lowercasing everything after
 *     the first letter for the filter control.
 */
export function captureStatusFilterLabel(status: CaptureStatus): string {
  const word = CAPTURE_STATUS_LABEL[status].replace(/^\S+\s*/, '');
  return word.charAt(0) + word.slice(1).toLowerCase();
}

export const CAPTURE_STATUS_FILTER_ORDER = CAPTURE_STATUS_ORDER;

/** What a resting chip says when nothing is chosen for it. */
export const CAPTURE_FILTER_RESTING_LABEL = {
  location: 'Location',
  date: 'Date',
  status: 'Status',
} as const;

/**
 * The "everything" option at the top of each chip's list.
 *
 * Named for the thing it clears rather than a bare "All", so a screen reader
 * hears which filter is being widened.
 */
export const CAPTURE_FILTER_ALL_LABEL = {
  location: 'All locations',
  date: 'All dates',
  status: 'All statuses',
} as const;

/**
 * What the no-results line says the list is currently narrowed by.
 *
 * The design shows the active selections joined by a middle dot, and falls back
 * to a nudge when — impossibly, given this only renders while filters are
 * active — nothing readable is selected. It is built from the SELECTIONS rather
 * than from the result set, so it stays true even when the result set is empty.
 */
export function describeActiveCaptureFilters(
  filters: CaptureFilters,
  locationLabel: string | null,
): string {
  const parts: string[] = [];
  const query = filters.query.trim();
  if (query !== '') parts.push(`\u201c${query}\u201d`);
  if (filters.locationKey !== null) parts.push(locationLabel ?? 'Location');
  if (filters.date !== null) parts.push(CAPTURE_DATE_LABEL[filters.date]);
  if (filters.status !== null) parts.push(captureStatusFilterLabel(filters.status));
  return parts.length > 0 ? parts.join(' · ') : 'Try widening the filters.';
}
